import { RequireAtLeastOne } from "common/types/utils";

import {
  pushEnrichedEventToDataLayerWithCleanUp,
  pushEventToDataLayer
} from "seneca-common/features/analytics";
import { AccessorRoleType } from "seneca-common/features/class/state/models";
import { withLogAndSwallowError } from "seneca-common/utils/senecaAnalytics";

import {
  InviteAllParentsTileAction,
  InviteClassParentsTileAction,
  SendTeacherParentInviteSource
} from "./consts";

export const analyticsLogSendParentInvite = withLogAndSwallowError(
  pushSendParentInviteToDataLayer
);

export const analyticsLogSendInviteError = withLogAndSwallowError(
  pushSendInviteErrorToDataLayer
);

export const analyticsLogSendInvite = withLogAndSwallowError(
  pushSendInviteToDataLayer
);

export const analyticsLogAcceptInvite = withLogAndSwallowError(
  pushAcceptInviteToDataLayer
);

export const analyticsLogRejectInvite = withLogAndSwallowError(
  pushRejectInviteToDataLayer
);

export const analyticsLogTransferOwnership = withLogAndSwallowError(
  pushTransferOwnershipToDataLayer
);

export const analyticsLogRemoveTeacher = withLogAndSwallowError(
  pushRemoveTeacherToDataLayer
);

export const analyticsLogLeaveClass = withLogAndSwallowError(
  pushLeaveClassToDataLayer
);

export const analyticsLogJoinSchoolClass = withLogAndSwallowError(
  pushJoinClassToDataLayer
);

export const analyticsLogJoinSchoolClasses = withLogAndSwallowError(
  pushJoinClassesToDataLayer
);

export const analyticsLogOpenManageStudents = withLogAndSwallowError(
  pushOpenManageStudentsToDataLayer
);

export const analyticsLogResetStudentPassword = withLogAndSwallowError(
  pushResetStudentPasswordToDataLayer
);

export const analyticsLogInviteAllParentsTile = withLogAndSwallowError(
  pushInviteAllParentsTile
);

export const analyticsLogPreviewInviteAllParents = withLogAndSwallowError(
  pushPreviewInviteAllParents
);

export const analyticsLogInviteClassParentsTile = withLogAndSwallowError(
  pushInviteClassParentsTile
);

const nameSpace = "s/classManagement";

type SendInviteProps = {
  inviteeUserId: string;
  inviteeRole: AccessorRoleType;
};

function pushSendInviteErrorToDataLayer(error: string) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/sendInviteError`,
    payload: {
      error
    }
  });
}

function pushSendInviteToDataLayer(payload: SendInviteProps) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/sendInvite`,
    payload
  });
}

export type SendParentInviteProps = {
  studentUserId?: string;
  parentId?: string;
  classId?: string;
  numParentsInvited?: number;
  inviteSource: SendTeacherParentInviteSource;
  externalSchoolId?: string | null;
};

function pushSendParentInviteToDataLayer(payload: SendParentInviteProps) {
  //dont log id if it's an email
  if (payload?.parentId?.includes("@")) {
    pushEnrichedEventToDataLayerWithCleanUp({
      event: `${nameSpace}/sendParentInvite`,
      payload: { ...payload, parentId: undefined, isAnonymous: true }
    });
  } else {
    pushEnrichedEventToDataLayerWithCleanUp({
      event: `${nameSpace}/sendParentInvite`,
      payload
    });
  }
}

type AcceptInviteProps = {
  classId: string;
  inviteeRole: AccessorRoleType;
};

function pushAcceptInviteToDataLayer(payload: AcceptInviteProps) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/acceptInvite`,
    payload
  });
}

type RejectInviteProps = {
  classId: string;
};

function pushRejectInviteToDataLayer(payload: RejectInviteProps) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/rejectInvite`,
    payload
  });
}

type TransferOwnershipProps = {
  manageeUserId: string;
};

function pushTransferOwnershipToDataLayer(payload: TransferOwnershipProps) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/transferOwnership`,
    payload
  });
}

type RemoveTeacherProps = {
  manageeUserId: string;
};

function pushRemoveTeacherToDataLayer(payload: RemoveTeacherProps) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/removeTeacher`,
    payload
  });
}

function pushLeaveClassToDataLayer() {
  pushEventToDataLayer({
    event: `${nameSpace}/leaveClass`
  });
}

type JoinSchoolClassProps = {
  classId: string;
};

function pushJoinClassToDataLayer(payload: JoinSchoolClassProps) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/joinSchoolClass`,
    payload
  });
}

type JoinSchoolClassesProps = {
  numClassesJoined: number;
};

function pushJoinClassesToDataLayer(payload: JoinSchoolClassesProps) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/joinSchoolClasses`,
    payload
  });
}

type OpenManageStudentsProps = {
  isManaged: boolean;
  source: string;
};

function pushOpenManageStudentsToDataLayer(payload: OpenManageStudentsProps) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/openManageStudents`,
    payload
  });
}

type ResetStudentPasswordPaylod = {
  classId: string;
  studentUserId: string;
};

function pushResetStudentPasswordToDataLayer(
  payload: ResetStudentPasswordPaylod
) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/resetStudentPassword`,
    payload
  });
}

type InviteAllParentsTilePayload = RequireAtLeastOne<{
  actionName: InviteAllParentsTileAction;
  inviteSource: SendTeacherParentInviteSource;
}>;

function pushInviteAllParentsTile(payload: InviteAllParentsTilePayload) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/inviteAllParentsTile`,
    payload
  });
}

type PreviewInviteAllParents = {
  inviteSource: SendTeacherParentInviteSource;
};

function pushPreviewInviteAllParents(payload: PreviewInviteAllParents) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/previewInvite`,
    payload
  });
}

type InviteClassParentsTilePayload = {
  actionName: InviteClassParentsTileAction;
};

function pushInviteClassParentsTile(payload: InviteClassParentsTilePayload) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${nameSpace}/inviteClassParentsTile`,
    payload
  });
}
